import React, { useState, useEffect, useRef } from 'react';
import { Search, X, Building, Star } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { serviceService } from '@/lib/api/service.service';
import type { Vendor, ServiceType } from '@/types/service.types';

interface VendorSearchInputProps {
  serviceType: ServiceType;
  selectedVendor?: Vendor | null;
  onVendorSelect: (vendor: Vendor | null) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
}

export const VendorSearchInput: React.FC<VendorSearchInputProps> = ({
  serviceType,
  selectedVendor,
  onVendorSelect,
  placeholder = "Search vendors...",
  disabled = false,
  required = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync searchTerm with selectedVendor (but don't trigger search)
  useEffect(() => {
    if (selectedVendor) {
      setSearchTerm(selectedVendor.name);
    } else {
      setSearchTerm('');
    }
  }, [selectedVendor]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchVendors = async (search: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await serviceService.searchVendors({
        search: search.length >= 3 ? search : undefined,
        service_type: serviceType,
        limit: 20
      });
      
      setVendors(result);
    } catch (err) {
      console.error('Error searching vendors:', err);
      setError('Failed to search vendors');
      setVendors([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    // Clear selection if user is typing and it doesn't match selected vendor
    if (selectedVendor && value !== selectedVendor.name) {
      onVendorSelect(null);
    }

    // Debounced search when user types
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (value.length >= 3) {
      searchTimeoutRef.current = setTimeout(() => {
        searchVendors(value);
      }, 300);
    } else if (value.length === 0) {
      // Clear vendors when input is cleared
      setVendors([]);
    }
  };

  const handleVendorSelect = (vendor: Vendor) => {
    setSearchTerm(vendor.name);
    setShowDropdown(false);
    onVendorSelect(vendor);
    inputRef.current?.blur();
  };

  const handleClearSelection = () => {
    setSearchTerm('');
    setShowDropdown(false);
    onVendorSelect(null);
    inputRef.current?.focus();
  };

  const handleInputFocus = () => {
    // Load vendors when user focuses on the field if we don't have any
    if (vendors.length === 0) {
      searchVendors('');
    }
    setShowDropdown(true);
  };

  const renderVendorItem = (vendor: Vendor) => (
    <div
      key={vendor.id}
      className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
      onClick={() => handleVendorSelect(vendor)}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <Building className="h-4 w-4 text-gray-400" />
            <span className="font-medium text-gray-900">{vendor.name}</span>
          </div>
          {vendor.contact_person && (
            <div className="text-sm text-gray-500 mt-1">
              Contact: {vendor.contact_person}
            </div>
          )}
          {vendor.address && (
            <div className="text-sm text-gray-500">
              {vendor.address}
            </div>
          )}
          {vendor.description && (
            <div className="text-sm text-gray-600 mt-1 truncate">
              {vendor.description}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 text-sm">
          <Star className="h-4 w-4 text-yellow-400 fill-current" />
          <span className="text-gray-600">{vendor.rating.toFixed(1)}</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="relative" ref={dropdownRef}>
      <Label htmlFor="vendor-search">Vendor (Optional)</Label>
      <div className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            ref={inputRef}
            id="vendor-search"
            type="text"
            value={searchTerm}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            placeholder={placeholder}
            disabled={disabled}
            required={required}
            className={`pl-10 ${selectedVendor ? 'pr-10' : ''} ${error ? 'border-red-300' : ''}`}
          />
          {selectedVendor && (
            <button
              type="button"
              onClick={handleClearSelection}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              title="Clear selection"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        
        {/* Helper text */}
        <div className="text-xs text-gray-500 mt-1">
          {loading && "Searching vendors..."}
          {error && <span className="text-red-500">{error}</span>}
        </div>
      </div>

      {/* Dropdown - only show when user focuses and we have results */}
      {showDropdown && vendors.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-64 overflow-y-auto">
          <div className="p-2 text-xs text-gray-500 bg-gray-50 border-b">
            {vendors.length} vendor{vendors.length !== 1 ? 's' : ''} found
          </div>
          {vendors.map(renderVendorItem)}
        </div>
      )}
    </div>
  );
};
